<mat-progress-bar [mode]="loading() || saving() ? 'indeterminate' : 'determinate'" [value]="100" class="page-progress" aria-label="Loading"></mat-progress-bar>

<lib-page-header [title]="mode() === 'create' ? 'New user data' : (form.controls['type'].value || '') + ' / ' + (form.controls['key'].value || '')"
                  screenName="User Data"
                  backTo="/scheduling/user-data"
                  backLabel="Back to user data">
    <mat-icon icon>{{ mode() === 'create' ? 'add_circle' : 'edit' }}</mat-icon>
    <ng-container actions>
        <button mat-stroked-button type="button"
                class="page-header-secondary-action"
                [disabled]="saving()"
                (click)="onReset()"
                matTooltip="Reset to last-saved values">
            <mat-icon>undo</mat-icon><span>Reset</span>
        </button>
        <button mat-flat-button color="primary" type="button"
                class="page-header-primary-action"
                [disabled]="!canSave()"
                (click)="onSave()"
                [matTooltip]="mode() === 'create' ? 'Create row' : 'Save changes'">
            <mat-icon>{{ mode() === 'create' ? 'add' : 'save' }}</mat-icon>
            <span>{{ mode() === 'create' ? 'Create' : 'Save' }}</span>
        </button>
    </ng-container>
</lib-page-header>

<section class="main-content edit-content">
  <div class="edit-content-panel app-panel">

    <!-- Two-tab body: Info (metadata) + Payload (JSON viewer + schema fields). -->
    <mat-tab-group class="edit-tabs"
                   animationDuration="0ms"
                   mat-stretch-tabs="false"
                   [selectedIndex]="activeTab()"
                   (selectedIndexChange)="onTabChange($event)">

        <!-- ─────────── Tab 1: Info ─────────── -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">badge</mat-icon> Info
            </ng-template>

            <div class="tab-body info-tab">
                <div class="edit-form-panel">
                    <form [formGroup]="form" class="ud-form">

                        <div class="form-section">
                            <div class="section-title">
                                <mat-icon>badge</mat-icon> Identity
                            </div>
                            <div class="ud-row two-col">
                                <mat-form-field appearance="outline">
                                    <mat-label>Type</mat-label>
                                    <mat-icon matPrefix class="field-icon">category</mat-icon>
                                    <input matInput formControlName="type"
                                           placeholder="e.g. NE / PROBE / CIRCUIT" />
                                    <mat-hint *ngIf="mode() === 'edit'">Type is fixed once a row exists</mat-hint>
                                    <mat-error *ngIf="form.controls['type'].hasError('required')">Type is required</mat-error>
                                </mat-form-field>
                                <mat-form-field appearance="outline">
                                    <mat-label>Key</mat-label>
                                    <mat-icon matPrefix class="field-icon">vpn_key</mat-icon>
                                    <input matInput formControlName="key"
                                           placeholder="e.g. NE-NYC-CORE-01" />
                                    <mat-hint *ngIf="mode() === 'edit'">Key is fixed once a row exists</mat-hint>
                                    <mat-error *ngIf="form.controls['key'].hasError('required')">Key is required</mat-error>
                                </mat-form-field>
                            </div>
                            <mat-form-field appearance="outline" class="ud-full">
                                <mat-label>Label</mat-label>
                                <mat-icon matPrefix class="field-icon">label</mat-icon>
                                <input matInput formControlName="label"
                                       placeholder="Friendly name shown in lists" />
                            </mat-form-field>
                            <div class="toggle-row">
                                <mat-slide-toggle formControlName="isResource" color="primary">
                                    Resource
                                </mat-slide-toggle>
                                <span class="toggle-hint">
                                    Toggle on when a runner must hold this row exclusively for an
                                    execution. Reveals the resource card below.
                                </span>
                            </div>
                        </div>

                        <div class="form-section" *ngIf="form.controls['isResource'].value">
                            <div class="section-title">
                                <mat-icon>inventory_2</mat-icon> Resource card
                            </div>
                            <div class="ud-row two-col">
                                <mat-form-field appearance="outline">
                                    <mat-label>Capacity</mat-label>
                                    <mat-icon matPrefix class="field-icon">inventory_2</mat-icon>
                                    <input matInput type="number" min="0" step="1"
                                           formControlName="capacity" placeholder="∞" />
                                    <mat-hint>Max simultaneous claims. Blank = unbounded.</mat-hint>
                                </mat-form-field>
                                <mat-form-field appearance="outline">
                                    <mat-label>Capacity remaining</mat-label>
                                    <mat-icon matPrefix class="field-icon">inventory</mat-icon>
                                    <input matInput type="number" min="0" step="1"
                                           formControlName="capacityRemaining" />
                                    <mat-hint>Burn-down counter (only used when reusable=false).</mat-hint>
                                </mat-form-field>
                            </div>
                            <div class="toggle-row">
                                <mat-slide-toggle formControlName="reusable" color="primary">
                                    Reusable (pool)
                                </mat-slide-toggle>
                                <span class="toggle-hint">
                                    Pool — capacity replenishes on release. Burn-down — each claim consumes a slot.
                                </span>
                            </div>
                            <div class="ud-row two-col">
                                <mat-form-field appearance="outline">
                                    <mat-label>Cost per use</mat-label>
                                    <mat-icon matPrefix class="field-icon">payments</mat-icon>
                                    <input matInput type="number" min="0" step="0.01"
                                           formControlName="costPerUse" placeholder="0.00" />
                                </mat-form-field>
                                <mat-form-field appearance="outline">
                                    <mat-label>Currency</mat-label>
                                    <mat-icon matPrefix class="field-icon">payments</mat-icon>
                                    <input matInput type="text" maxlength="3"
                                           formControlName="costCurrency"
                                           placeholder="USD"
                                           style="text-transform: uppercase" />
                                    <mat-hint>ISO 4217</mat-hint>
                                </mat-form-field>
                            </div>
                            <mat-expansion-panel class="availability-panel" [expanded]="false">
                                <mat-expansion-panel-header>
                                    <mat-panel-title>
                                        <mat-icon class="panel-icon">schedule</mat-icon>
                                        Availability policy
                                    </mat-panel-title>
                                    <mat-panel-description>
                                        Always available unless overridden.
                                    </mat-panel-description>
                                </mat-expansion-panel-header>
                                <mat-form-field appearance="outline" class="ud-full availability-field">
                                    <mat-label>Availability JSON</mat-label>
                                    <mat-icon matPrefix class="field-icon">schedule</mat-icon>
                                    <textarea matInput rows="4" wrap="off"
                                              formControlName="availability"
                                              placeholder='{ "timezone": "America/Toronto", "days": [1,2,3,4,5], "windows": [{"start":"09:00","end":"17:00"}] }'></textarea>
                                </mat-form-field>
                            </mat-expansion-panel>
                        </div>

                        <div class="form-section">
                            <div class="section-title">
                                <mat-icon>groups</mat-icon> Org scoping
                                <span class="section-hint">All three optional. Empty = not scoped at that level.</span>
                            </div>
                            <div class="ud-row three-col">
                                <mat-form-field appearance="outline">
                                    <mat-label>Financial unit</mat-label>
                                    <mat-icon matPrefix class="field-icon">account_balance</mat-icon>
                                    <input matInput formControlName="financialUnit" />
                                </mat-form-field>
                                <mat-form-field appearance="outline">
                                    <mat-label>Org unit</mat-label>
                                    <mat-icon matPrefix class="field-icon">business</mat-icon>
                                    <input matInput formControlName="orgUnit" />
                                </mat-form-field>
                                <mat-form-field appearance="outline">
                                    <mat-label>Department</mat-label>
                                    <mat-icon matPrefix class="field-icon">groups</mat-icon>
                                    <input matInput formControlName="department" />
                                </mat-form-field>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </mat-tab>

        <!-- ─────────── Tab 2: Payload (JSON | Fields split) ─────────── -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">data_object</mat-icon> Payload
            </ng-template>

            <div class="tab-body payload-tab">
              <div class="payload-content">
                <div class="payload-split" [class.payload-split--no-json]="!showJsonPane()">

                    <!-- Sub-panel A — JSON viewer / editor.  Hidden when the
                         operator clicks the pane-toggle button on the right
                         sub-panel; the column-browser then occupies the
                         full width. -->
                    <div class="payload-json-sub" *ngIf="showJsonPane()">
                        <div class="sub-header">
                            <mat-icon>data_object</mat-icon>
                            <span class="sub-title">JSON viewer</span>
                            <span class="flex-spacer"></span>
                            <button type="button" mat-icon-button class="sub-btn"
                                    (click)="toggleJsonEdit()"
                                    [disabled]="editingJson() && jsonError() !== null"
                                    [matTooltip]="editingJson()
                                        ? (jsonError() !== null ? 'Fix JSON to return to viewer' : 'Done editing')
                                        : 'Edit JSON'"
                                    [attr.aria-label]="editingJson() ? 'Return to JSON viewer' : 'Edit JSON'">
                                <mat-icon>{{ editingJson() ? 'check' : 'edit' }}</mat-icon>
                            </button>
                        </div>
                        <div class="sub-body" [class.has-error]="editingJson() && jsonError() !== null">
                            <ng-container *ngIf="!editingJson(); else editJson">
                                <app-json-path-viewer *ngIf="parsedJson() !== null; else emptyJson"
                                                       [value]="parsedJson()"
                                                       [highlightedPath]="focusedPath()"
                                                       (nodeClick)="onJsonNodeClick($event)" />
                                <ng-template #emptyJson>
                                    <div class="empty-hint">No payload — click the pencil to add one.</div>
                                </ng-template>
                            </ng-container>
                            <ng-template #editJson>
                                <textarea class="json-input"
                                          [formControl]="$any(form.controls['data'])"
                                          spellcheck="false"
                                          autocomplete="off"
                                          autocapitalize="off"
                                          autocorrect="off"
                                          wrap="off"
                                          placeholder='{ "vendor": "Cisco", "model": "ASR9904" }'></textarea>
                            </ng-template>
                        </div>
                        <div class="sub-foot" *ngIf="editingJson() && jsonError()">
                            <mat-icon class="err-icon">error</mat-icon>
                            <span class="err-text">{{ jsonError() }}</span>
                        </div>
                    </div>

                    <!-- Sub-panel B — Schema fields. Built from the union of
                         declared attributes + the payload's observed keys, so
                         the panel is useful even for types that have never
                         been edited via the admin page. -->
                    <div class="payload-schema-sub">
                        <div class="sub-header">
                            <mat-icon>schema</mat-icon>
                            <span class="sub-title">Schema fields</span>
                            <span class="flex-spacer"></span>
                            <span class="sub-hint" *ngIf="attributes().length > 0">
                                Declared on <code>{{ form.controls['type'].value }}</code>
                            </span>
                            <span class="sub-hint" *ngIf="attributes().length === 0 && schemaForType()">
                                Auto-detected from payload
                            </span>
                            <button type="button" mat-icon-button class="sub-btn"
                                    (click)="toggleJsonPane()"
                                    [matTooltip]="showJsonPane() ? 'Hide JSON viewer (full-width edit)' : 'Show JSON viewer'"
                                    [attr.aria-label]="showJsonPane() ? 'Hide JSON viewer' : 'Show JSON viewer'">
                                <mat-icon>{{ showJsonPane() ? 'fullscreen' : 'fullscreen_exit' }}</mat-icon>
                            </button>
                        </div>
                        <div class="sub-body sub-body--columns">
                            <app-schema-column-browser *ngIf="schemaForType() as sch"
                                             [schema]="sch"
                                             [value]="schemaValue()"
                                             (valueChange)="onSchemaFormChange($event)"
                                             (focusPath)="onSchemaFieldFocus($event)"
                                             (pathCleared)="onPathCleared($event)">
                            </app-schema-column-browser>
                            <div *ngIf="!schemaForType()" class="empty-hint">
                                No fields yet. Add JSON keys in the viewer on the left,
                                or declare attributes on the type admin page — labelled
                                inputs will appear here automatically.
                            </div>
                        </div>
                    </div>

                </div>
              </div>
            </div>
        </mat-tab>

    </mat-tab-group>
  </div>
</section>
